import { approveByHSESlopsDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approveByHSESlopsDisposalSchema";
import { closeHSESlopsDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/closeHSESlopsDisposalSchema";
import { startHSESlopsDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/startHSESlopsDisposalSchema";
import type { PortServiceFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/statusKeys";
import type { MarpolAnnexI } from "../../store/usePortServicesForm.store";
import { approveByHSESludgeDisposalSchema } from "../schemas/approveByHSESludgeDisposalSchema";
import { approvedByMDSludgeDisposalSchema } from "../schemas/approvedByMDSludgeDisposalSchema";
import { closeSludgeDisposalSchema } from "../schemas/closeSludgeDisposalSchema";
import { rejectionSchema } from "../schemas/rejectionSchema";
import { startSludgeDisposalSchema } from "../schemas/startSludgeDisposalSchema";

export const WASTE_DISPOSAL_SLUDGE_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["HSE"],
    canReject: ["HSE"],
    nextStatus: STATUS_KEYS.HSE_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Marine Operator", "Marine Supervisor", "Dry Bulk"],
    statusesList: {
      [STATUS_KEYS.HSE_APPROVED]: {
        label: "Approved by HSE",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesWasteDisposalSludge",
        extraFields: ["specialInstructions", "notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesWasteDisposalSludge",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.HSE_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesWasteDisposalSludge",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesWasteDisposalSludge",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortServicesWasteDisposalSludge",
        extraFields: ["ats", "etc"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortServicesWasteDisposalSludge",
        extraFields: [
          "atc",
          "wasteResiduesForDischargeFacility",
          "actualNumberOfSkips",
          "images",
          "serviceReportAttachment",
        ],
      },
    },
  },
};

export const HSE_WASTE_DISPOSAL_SLOPS_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["HSE"],
    canReject: ["HSE"],
    nextStatus: STATUS_KEYS.HSE_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    statusesList: {
      [STATUS_KEYS.HSE_APPROVED]: {
        label: "Approved by HSE",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesHseWasteDisposalSlops",
        extraFields: ["specialInstructions", "notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesHseWasteDisposalSlops",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.HSE_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortServicesHseWasteDisposalSlops",
        extraFields: ["ats", "etc", "ets"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesHseWasteDisposalSlops",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    canCancel: [
      {
        userRoles: ["hse"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortServicesHseWasteDisposalSlops",
        extraFields: [
          "atc",
          "wasteResiduesForDischargeFacility",
          "slopsDisposalDocuments",
          "serviceReportAttachment",
        ],
      },
    },
  },
};

export const validateSludgeDisposalExtraFields = ({
  newStatusOption,
  notifyOption,
  specialInstructions,
  wasteResiduesForDischargeFacility,
  actualNumberOfSkips,
  notes,
  internalNotes,
  externalNotes,
  rejectionReason,
  flowAts,
  flowEtc,
  flowAtc,
  images,
  serviceReportAttachment,
  attachment,
}: {
  newStatusOption: string;
  notifyOption: string[];
  specialInstructions: string;
  wasteResiduesForDischargeFacility?: MarpolAnnexI[];
  actualNumberOfSkips?: number;
  notes: string;
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  images: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }[];
  serviceReportAttachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.HSE_APPROVED:
      return approveByHSESludgeDisposalSchema().safeParse({
        specialInstructions: specialInstructions || "",
        attachment,
        notes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.MD_APPROVED:
      return approvedByMDSludgeDisposalSchema().safeParse({
        notificationOption: notifyOption,
        internalNotes,
        externalNotes,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startSludgeDisposalSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeSludgeDisposalSchema(flowAts).safeParse({
        atc: flowAtc,
        notificationOption: notifyOption,
        images,
        serviceReportAttachment,
        wasteResiduesForDischargeFacility,
        actualNumberOfSkips,
      });

    default:
      return null;
  }
};

export const validateHSESlopsDisposalExtraFields = ({
  newStatusOption,
  notifyOption,
  specialInstructions,
  wasteResiduesForDischargeFacility,
  notes,
  rejectionReason,
  flowAts,
  flowEtc,
  flowAtc,
  slopsDisposalDocuments,
  serviceReportAttachment,
  attachment,
}: {
  newStatusOption: string;
  notifyOption: string[];
  specialInstructions: string;
  wasteResiduesForDischargeFacility?: MarpolAnnexI[];
  notes: string;
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  slopsDisposalDocuments: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }[];
  serviceReportAttachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.HSE_APPROVED:
      return approveByHSESlopsDisposalSchema().safeParse({
        specialInstructions: specialInstructions || "",
        attachment,
        notes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startHSESlopsDisposalSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeHSESlopsDisposalSchema(flowAts).safeParse({
        atc: flowAtc,
        notificationOption: notifyOption,
        slopsDisposalDocuments,
        serviceReportAttachment,
        wasteResiduesForDischargeFacility,
      });

    default:
      return null;
  }
};

export const isHSE = (category: string) => {
  return category === "HSE";
};

export const isHSEWasteDisposal = (category: string, type: string) => {
  return isHSE(category) && type === "Waste Disposal";
};

export const isHSEWasteDisposalSludge = (category: string, type: string, subType: string) => {
  return isHSEWasteDisposal(category, type) && subType === "Sludge";
};

export const isHSEWasteDisposalSlops = (category: string, type: string, subType: string) => {
  return isHSEWasteDisposal(category, type) && subType === "Slops";
};

export function canUserCancelService(
  userRoles: string[],
  userCanCancelPortService: boolean,
  currentStatus: string,
  flow: PortServiceFlow | null,
): boolean {
  if (!userCanCancelPortService || !flow || !flow[currentStatus]) {
    return false;
  }

  const cancelRules = flow[currentStatus].canCancel!;

  if (!cancelRules) {
    return false;
  }

  return cancelRules.some((rule) => {
    return !rule.userRoles || rule.userRoles.some((role) => userRoles.includes(role));
  });
}
